import {EditorState, type Extension, RangeSetBuilder, StateField} from '@codemirror/state'
import {Decoration, type DecorationSet, EditorView} from '@codemirror/view'
import {clsx} from 'clsx'
import type {Delimiter, Nodes as Node} from 'mdast'
import {visitParents} from 'unist-util-visit-parents'

import {isPositionedNode} from '../../markdown/positioned-node'
import {CharacterReferenceWidget} from '../widgets/character-reference'
import {EmptySpanWidget} from '../widgets/empty-span'
import {ImageWidget} from '../widgets/image'
import {ThematicBreakWidget} from '../widgets/thematic-break'
import {activeNodesChanged, activeNodesExtension, getActiveNodes} from './active-nodes'
import styles from './decorate-markdown.module.css'
import {getMarkdownTree, markdownParserExtension, markdownTreeChanged} from './markdown-parser'

/** Ancestor node types with data about whether they are active or not. For handling children of active nodes. */
type NodeContext = Array<{type: Node['type']; active: boolean}>

const withinActiveList = (context: NodeContext) => context.some(parent => parent.active && parent.type === 'list')

const withinActiveTable = (context: NodeContext) => context.some(parent => parent.type === 'table' && parent.active)

// `root` is always active when the editor is focused since it covers the entire document
const withinActiveBlock = (context: NodeContext) => context.some(parent => parent.type !== 'root' && parent.active)

const isTopLevelBlock = (context: NodeContext) => context.length === 1 && context[0]?.type === 'root'

// These aren't exported so we have to extract them from the function parameters :(
type DecorationSpec = Parameters<typeof Decoration.mark>[0] | Parameters<typeof Decoration.replace>[0]

function getNodeMarks(node: Node, active: boolean, context: NodeContext): DecorationSpec[] | undefined {
  const blockClass = clsx(styles.block, active && styles.activeBlock)

  switch (node.type) {
    case 'delimiter': {
      const delimiter = getDelimiterMark(node, context)
      return delimiter ? [delimiter] : undefined
    }
    /*
     * Mark Decorations
     */
    case 'heading':
      return [{tagName: `h${node.depth}`, class: blockClass}]
    case 'link':
      if (!withinActiveBlock(context)) return [{tagName: 'a', attributes: {href: node.url, title: node.title ?? ''}}]
      break
    case 'blockquote':
      return [{tagName: 'blockquote', class: clsx(blockClass, styles.blockquote)}]
    case 'code':
      return [{tagName: 'pre', class: clsx(blockClass, styles.backgroundColorEditables)}, {tagName: 'code'}]
    case 'inlineCode':
      return [{class: styles.backgroundColorEditables, tagName: 'code'}]
    case 'html':
      return [{tagName: 'code', class: clsx(styles.html, node.data?.isBlock && blockClass)}]
    case 'emphasis':
      return [{tagName: 'em'}]
    case 'delete':
      return [{tagName: 'del'}]
    case 'strong':
      return [{tagName: 'strong'}]
    case 'listItem':
      if (!withinActiveList(context)) return [{tagName: 'li'}]
      break
    case 'list':
      if (!withinActiveList(context)) return [{tagName: node.ordered ? 'ol' : 'ul', class: blockClass}]
      break
    case 'paragraph': {
      // When a user sets a placeholder, adding the initial input and removing the placeholder can cause the cursor to jump to the
      // end of the document instead of staying at the beginning. To address this, we wrap top-level paragraphs in a `div`, ensuring
      // the cursor remains positioned correctly during initial input, while keeping margins and spacing intact.
      const isTopLevel = isTopLevelBlock(context)
      return isTopLevel
        ? [
            {tagName: 'div', class: blockClass},
            {tagName: 'p', class: styles.paragraph},
          ]
        : [{tagName: 'p', class: styles.paragraph}]
    }
    case 'table':
      // tables revert to plain text when active (for now, until we build a table editor)
      return active
        ? [{tagName: 'pre', class: blockClass}]
        : [
            {
              tagName: 'table',
              class: clsx(blockClass, styles.table),
              // contenteditable must be disabled for the `white-space` styling to take effect, which is necessary to
              // collapse extra whitespace typically used to align table delimiters in the Markdown source, particularly
              // for right- and center-aligned columns
              attributes: {contenteditable: 'false'},
            },
          ]
    case 'tableRow':
      if (!withinActiveTable(context)) return [{tagName: 'tr', class: styles.backgroundColorEditables}]
      break
    case 'tableCell':
      if (!withinActiveTable(context))
        return [
          {
            tagName: node.data?.isHeader ? 'th' : 'td',
            class: clsx({
              [styles.alignRight]: node.data?.alignment === 'right',
              [styles.alignCenter]: node.data?.alignment === 'center',
            }),
          },
        ]
      break

    /*
     * Widget decorations
     */
    case 'image':
      // When not active, render as image widget
      return active
        ? [{tagName: 'div', class: blockClass}]
        : [
            {
              widget: new ImageWidget(node.url, node.alt ?? 'Embedded image', node.title ?? undefined),
              block: false,
            },
          ]
    case 'thematicBreak':
      // When not active, replace with horizontal rule widget
      return active
        ? [{tagName: 'div', class: blockClass}]
        : [
            {
              widget: ThematicBreakWidget,
              block: false,
            },
          ]
  }
}

function getDelimiterMark(delimiter: Delimiter, context: NodeContext): DecorationSpec | undefined {
  switch (delimiter.tokenType) {
    case 'tableDelimiter':
      return withinActiveTable(context) ? {class: styles.delimiter} : {tagName: 'td'}
    case 'tableDelimiterRow':
      return withinActiveTable(context) ? {class: styles.delimiter} : {tagName: 'tr', class: styles.delimiter}
    case 'characterReference':
      return withinActiveBlock(context)
        ? {class: styles.delimiter}
        : {widget: new CharacterReferenceWidget(delimiter.raw)}
    case 'resourceDestination':
      return {tagName: 'a', attributes: {href: delimiter.raw}}
    default:
      return {class: styles.delimiter}
  }
}

function buildDecorations(state: EditorState): DecorationSet {
  const tree = getMarkdownTree(state)
  const activeNodes = getActiveNodes(state)

  const builder = new RangeSetBuilder<Decoration>()

  visitParents(tree, (node, parents) => {
    if (!isPositionedNode(node)) return

    const nodeContext: NodeContext = parents.map(p => ({type: p.type, active: activeNodes.has(p)}))

    const markSpecs = getNodeMarks(node, activeNodes.has(node), nodeContext)
    if (markSpecs) {
      for (const [index, markSpec] of markSpecs.entries()) {
        // Use `inclusive: true` so the mark includes its start and end positions.
        // This prevents rendering issues when a replace decoration is applied at a node's start or end
        // and would otherwise conflict with the mark.
        const mark =
          'widget' in markSpec ? Decoration.replace(markSpec) : Decoration.mark({...markSpec, inclusive: true})

        // For decorations with the same 'from' and 'to' value, CodeMirror renders the first tag in the set and wraps
        // the second tag around it. However, our tree walker encounters parents first and then children, which causes
        // incorrect element order. For example, a list with one element would result in `<li><ol>...</ol></li>`
        // because the list and item have the same start and end offsets.
        //
        // To fix this, CodeMirror supports a secondary comparison by `startSide`. Lower `startSide` = outermost
        // element.
        mark.startSide = parents.length + index / markSpecs.length
        builder.add(node.position.start.offset, node.position.end.offset, mark)
      }
    }
  })

  // Place an empty `span` element at the very end of the document, following any line breaks. The DOM will always
  // suppress a line break at the end of a block container, which would prevent the user from typing a newline at the
  // end of the doc. By placing an invisible span after the last character, we ensure that a trailing line break is
  // never the last thing in the document.
  if (isPositionedNode(tree))
    builder.add(
      tree.position.end.offset,
      tree.position.end.offset,
      Decoration.widget({
        widget: EmptySpanWidget,
        // `side` determines where the cursor ends up relative to this element. Positive ensures the cursor appears before
        // the element, ensuring that it remains 'attached' to text. This also determines order if two decorations have
        // the same position, so we use the max value to ensure this always renders at the very end of the doc.
        side: 10_000,
      }),
    )

  return builder.finish()
}

const markdownDecorationsState = StateField.define<DecorationSet>({
  create: buildDecorations,
  update: (prev, tr) => {
    const treeChanged = markdownTreeChanged(tr)

    if (tr.docChanged && !treeChanged) {
      // 'mapping' the decorations just updates their positions based on where text was inserted/deleted to keep them
      // relatively in sync position-wise. It prevents text from flowing out of the end of nodes, but doesn't
      // actually reflect any Markdown formatting changes. For that, we wait until the tree parse event which we know
      // will quickly follow a docChanged event.
      return prev.map(tr.changes)
    } else if (treeChanged || activeNodesChanged(tr)) {
      return buildDecorations(tr.state)
    } else {
      return prev
    }
  },
  provide: field => [markdownParserExtension, activeNodesExtension, EditorView.decorations.from(field)],
})

export const decorateMarkdownExtension: Extension = markdownDecorationsState
